// Enhanced loading component for code-split chunks
import React, { useState, useEffect } from 'react';
import { 
  Box, 
  CircularProgress, 
  Typography, 
  LinearProgress,
  Fade,
  Skeleton,
  Stack
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';

interface ChunkLoaderProps {
  chunkName?: string;
  progress?: number;
  timeout?: number;
  showProgress?: boolean;
  size?: 'small' | 'medium' | 'large';
  variant?: 'circular' | 'linear' | 'skeleton';
}

const ChunkLoader: React.FC<ChunkLoaderProps> = ({
  chunkName = 'Component',
  progress,
  timeout = 10000,
  showProgress = true,
  size = 'medium',
  variant = 'circular'
}) => {
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isTimedOut, setIsTimedOut] = useState(false);

  useEffect(() => {
    // Simulate progress if not provided
    if (progress === undefined) {
      const interval = setInterval(() => {
        setLoadingProgress(prev => {
          if (prev >= 90) return prev;
          return prev + Math.random() * 15;
        });
      }, 200);

      return () => clearInterval(interval);
    } else {
      setLoadingProgress(progress);
    }
  }, [progress]);

  useEffect(() => {
    // Handle timeout
    const timer = setTimeout(() => {
      setIsTimedOut(true);
    }, timeout);

    return () => clearTimeout(timer);
  }, [timeout]);

  const getSizeProps = () => {
    switch (size) {
      case 'small':
        return { width: 24, height: 24 };
      case 'large':
        return { width: 64, height: 64 };
      default:
        return { width: 40, height: 40 };
    }
  };

  const renderLoader = () => {
    if (isTimedOut) {
      return (
        <Box sx={{ textAlign: 'center', p: 3 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Taking longer than expected...
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Check your connection and try refreshing
          </Typography>
        </Box>
      );
    }

    switch (variant) {
      case 'linear':
        return (
          <Box sx={{ width: '100%', p: 2 }}>
            <LinearProgress 
              variant={progress ? 'determinate' : 'indeterminate'} 
              value={loadingProgress}
              sx={{ mb: 1 }}
            />
            {showProgress && (
              <Typography variant="caption" color="text.secondary">
                Loading {chunkName}... {Math.round(loadingProgress)}%
              </Typography>
            )}
          </Box>
        );

      case 'skeleton':
        return (
          <Box sx={{ p: 2 }}>
            <Stack spacing={1}>
              <Skeleton variant="rectangular" height={60} />
              <Skeleton variant="text" width="80%" />
              <Skeleton variant="text" width="60%" />
              <Skeleton variant="rectangular" height={40} />
            </Stack>
          </Box>
        );

      default:
        return (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            p: 3 
          }}>
            <CircularProgress 
              size={getSizeProps().width} 
              thickness={4}
              sx={{ mb: 2 }}
            />
            {showProgress && (
              <Typography variant="body2" color="text.secondary">
                Loading {chunkName}...
              </Typography>
            )}
          </Box>
        );
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.3 }}
      >
        <Fade in={true} timeout={300}>
          {renderLoader()}
        </Fade>
      </motion.div>
    </AnimatePresence>
  );
};

// Specialized loaders for different component types
export const DashboardLoader = () => (
  <ChunkLoader 
    chunkName="Dashboard" 
    variant="skeleton" 
    size="large"
  />
);

export const PageLoader = () => (
  <ChunkLoader 
    chunkName="Page" 
    variant="linear" 
    size="medium"
  />
);

export const DialogLoader = () => (
  <ChunkLoader 
    chunkName="Dialog" 
    variant="circular" 
    size="small"
  />
);

export const HeavyComponentLoader = () => (
  <ChunkLoader 
    chunkName="AI Component" 
    variant="linear" 
    size="large"
    timeout={15000}
  />
);

export default ChunkLoader;
